import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useData } from '../contexts/DataContext';
import { Trophy, Star, MapPin, ArrowLeft, Filter } from 'lucide-react';

const Container = styled.div`
  padding: 20px;
  max-width: 800px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 15px;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 30px;
  position: relative;
`;

const BackButton = styled.button`
  position: absolute;
  left: 0;
  top: 0;
  background: #5752e8;
  color: white;
  border: none;
  padding: 10px 15px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.9rem;
  transition: background 0.2s;
  
  &:hover {
    background: #5752e8;
  }
  
  @media (max-width: 768px) {
    position: static;
    margin-bottom: 15px;
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  color: #2c3e50;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const Subtitle = styled.p`
  color: #7f8c8d;
  font-size: 0.95rem;
`;

const FilterSection = styled.div`
  background: #f8f9fa;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 25px;
  
  @media (max-width: 768px) {
    padding: 15px;
  }
`;

const FilterTitle = styled.div`
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1rem;
`;

const FilterGroup = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: ${props => props.last ? '0' : '15px'};
`;

const FilterButton = styled.button`
  background: ${props => props.active ? '#5752e8' : 'white'};
  color: ${props => props.active ? 'white' : '#2c3e50'};
  border: 2px solid ${props => props.active ? '#5752e8' : '#ddd'};
  padding: 8px 16px;
  border-radius: 20px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.active ? '#5752e8' : '#f0f0f0'};
    border-color: ${props => props.active ? '#5752e8' : '#bbb'};
  }
`;

const FilterLabel = styled.label`
  font-size: 0.9rem;
  color: #555;
  margin-bottom: 8px;
  display: block;
`;

const FilterInput = styled.input`
  width: 100%;
  padding: 10px;
  border: 2px solid #ddd;
  border-radius: 8px;
  font-size: 0.9rem;
  
  &:focus {
    outline: none;
    border-color: #5752e8;
  }
`;

const AutocompleteContainer = styled.div`
  position: relative;
`;

const SuggestionsList = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 2px solid #5752e8;
  border-top: none;
  border-radius: 0 0 8px 8px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
`;

const SuggestionItem = styled.div`
  padding: 10px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background 0.2s;
  
  &:hover {
    background: #f0f0f0;
  }
  
  &:last-child {
    border-radius: 0 0 8px 8px;
  }
`;

const NoResults = styled.div`
  padding: 10px;
  color: #999;
  font-size: 0.9rem;
  text-align: center;
`;

const LeaderboardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;
const LeaderboardItem = styled.div`
  background: ${props => {
    if (props.rank === 1) return '#faaf40';
    if (props.rank === 2) return '#a9a9a9';
    if (props.rank === 3) return '#c56a2e';
    return 'white';
  }};
  padding: 20px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 15px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  @media (max-width: 768px) {
    padding: 15px;
    gap: 12px;
  }
`;

const RankBadge = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  min-width: 40px;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
    min-width: 30px;
  }
`;

const Content = styled.div`
  flex: 1;
`;

const Name = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 5px;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const Details = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 0.9rem;
  opacity: ${props => props.rank <= 3 ? '0.95' : '0.8'};
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
    gap: 8px;
  }
`;

const Detail = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Stats = styled.div`
  text-align: center;
  min-width: 80px;
`;

const StatValue = styled.div`
  font-size: 1.8rem;
  font-weight: bold;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const StatLabel = styled.div`
  font-size: 0.8rem;
  opacity: ${props => props.rank <= 3 ? '0.9' : '0.7'};
  margin-top: 2px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #7f8c8d;
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 20px;
  opacity: 0.3;
`;

// Major cities with coordinates for filtering
const MAJOR_CITIES = {
  'Kansas City': { lat: 39.0997, lng: -94.5786, state: 'Missouri' },
  'New York': { lat: 40.7128, lng: -74.0060, state: 'New York' },
  'Los Angeles': { lat: 34.0522, lng: -118.2437, state: 'California' },
  'Chicago': { lat: 41.8781, lng: -87.6298, state: 'Illinois' },
  'Houston': { lat: 29.7604, lng: -95.3698, state: 'Texas' },
  'Phoenix': { lat: 33.4484, lng: -112.0740, state: 'Arizona' },
  'Philadelphia': { lat: 39.9526, lng: -75.1652, state: 'Pennsylvania' },
  'San Antonio': { lat: 29.4241, lng: -98.4936, state: 'Texas' },
  'San Diego': { lat: 32.7157, lng: -117.1611, state: 'California' },
  'Dallas': { lat: 32.7767, lng: -96.7970, state: 'Texas' },
};

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming'
];

function PorcelainThrone() {
  const navigate = useNavigate();
  const { restrooms } = useData();
  const [topRestrooms, setTopRestrooms] = useState([]);
  const [filterType, setFilterType] = useState('global'); // 'global', 'city', 'state', 'country'
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [citySearchInput, setCitySearchInput] = useState('');
  const [stateSearchInput, setStateSearchInput] = useState('');
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);
  const [showStateSuggestions, setShowStateSuggestions] = useState(false);

  // Clear inputs when filter type changes
  useEffect(() => {
    setCitySearchInput('');
    setStateSearchInput('');
    setSelectedCity('');
    setSelectedState('');
    setShowCitySuggestions(false);
    setShowStateSuggestions(false);
  }, [filterType]);

  // Calculate distance between two coordinates (Haversine formula)
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 3959; // Earth's radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  useEffect(() => {
    // Calculate top restrooms by reviews in last 24 hours
    const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
    
    let filteredRestrooms = [...restrooms];

    // Apply location filter
    if (filterType === 'city' && selectedCity && MAJOR_CITIES[selectedCity]) {
      const cityCoords = MAJOR_CITIES[selectedCity];
      filteredRestrooms = restrooms.filter(r => {
        const distance = calculateDistance(
          cityCoords.lat,
          cityCoords.lng,
          r.latitude,
          r.longitude
        );
        return distance <= 50; // 50 mile radius
      });
    } else if (filterType === 'state' && selectedState) {
      // Filter by state (requires geocoding or stored state data)
      // For now, filter by address containing state name
      filteredRestrooms = restrooms.filter(r => 
        r.address && r.address.includes(selectedState)
      );
    } else if (filterType === 'country') {
      // Filter by country (default to USA for now)
      // In production, you'd filter by actual country field
      filteredRestrooms = restrooms; // All US for now
    }
    
    const restroomsWithRecentReviews = filteredRestrooms.map(restroom => {
      const recentReviews = (restroom.reviews || []).filter(review => {
        const reviewDate = new Date(review.createdAt?.toDate?.() || review.createdAt);
        return reviewDate >= twentyFourHoursAgo;
      });
      
      return {
        ...restroom,
        todayReviewCount: recentReviews.length
      };
    });
    
    // Sort by recent review count, then by average rating
    const sorted = restroomsWithRecentReviews
      .filter(r => r.todayReviewCount > 0)
      .sort((a, b) => {
        if (b.todayReviewCount !== a.todayReviewCount) {
          return b.todayReviewCount - a.todayReviewCount;
        }
        return (b.averageRating || 0) - (a.averageRating || 0);
      })
      .slice(0, 10);
    
    setTopRestrooms(sorted);
  }, [restrooms, filterType, selectedCity, selectedState]);

  // Filter cities based on search input
  const filteredCities = Object.keys(MAJOR_CITIES)
    .filter(city => city.toLowerCase().includes(citySearchInput.toLowerCase()))
    .sort();

  // Filter states based on search input
  const filteredStates = US_STATES
    .filter(state => state.toLowerCase().includes(stateSearchInput.toLowerCase()));

  // Handle city selection from suggestions
  const handleCitySelect = (city) => {
    setSelectedCity(city);
    setCitySearchInput(city);
    setShowCitySuggestions(false);
  };

  // Handle state selection from suggestions
  const handleStateSelect = (state) => {
    setSelectedState(state);
    setStateSearchInput(state);
    setShowStateSuggestions(false);
  };

  // Handle city input change
  const handleCityInputChange = (e) => {
    const value = e.target.value;
    setCitySearchInput(value);
    setShowCitySuggestions(true);
    
    // Clear selection if input doesn't match
    if (!Object.keys(MAJOR_CITIES).includes(value)) {
      setSelectedCity('');
    }
  };

  // Handle state input change
  const handleStateInputChange = (e) => {
    const value = e.target.value;
    setStateSearchInput(value);
    setShowStateSuggestions(true);
    
    // Clear selection if input doesn't match
    if (!US_STATES.includes(value)) {
      setSelectedState('');
    }
  };

  const renderStars = (rating) => {
    return (
      <Detail>
        <Star size={14} fill="currentColor" />
        <span>{rating.toFixed(1)}</span>
      </Detail>
    );
  };

  const getSubtitle = () => {
    if (filterType === 'city' && selectedCity) {
      return `Top Restrooms in ${selectedCity} (50 mile radius) • Last 24 Hours`;
    } else if (filterType === 'state' && selectedState) {
      return `Top Restrooms in ${selectedState} • Last 24 Hours`;
    } else if (filterType === 'country') {
      return `Top Restrooms in USA • Last 24 Hours`;
    }
    return "Top Rated Restrooms • Last 24 Hours";
  };

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate('/app')}>
          <ArrowLeft size={18} />
          Back to Map
        </BackButton>
        <Title>
          <Trophy size={32} />
          Porcelain Throne
        </Title>
        <Subtitle>{getSubtitle()}</Subtitle>
      </Header>

      <FilterSection>
        <FilterTitle>
          <Filter size={18} />
          Filter by Location
        </FilterTitle>
        
        <FilterGroup>
          <FilterButton 
            active={filterType === 'global'}
            onClick={() => setFilterType('global')}
          >
            🌍 Global
          </FilterButton>
          <FilterButton 
            active={filterType === 'city'}
            onClick={() => setFilterType('city')}
          >
            🏙️ City
          </FilterButton>
          <FilterButton 
            active={filterType === 'state'}
            onClick={() => setFilterType('state')}
          >
            🗺️ State
          </FilterButton>
          <FilterButton 
            active={filterType === 'country'}
            onClick={() => setFilterType('country')}
          >
            🌎 Country
          </FilterButton>
        </FilterGroup>

        {filterType === 'city' && (
          <AutocompleteContainer>
            <FilterLabel>Search City:</FilterLabel>
            <FilterInput
              type="text"
              placeholder="Start typing a city name..."
              value={citySearchInput}
              onChange={handleCityInputChange}
              onFocus={() => setShowCitySuggestions(true)}
              onBlur={() => setTimeout(() => setShowCitySuggestions(false), 200)}
            />
            {showCitySuggestions && citySearchInput && (
              <SuggestionsList>
                {filteredCities.length > 0 ? (
                  filteredCities.map(city => (
                    <SuggestionItem
                      key={city}
                      onClick={() => handleCitySelect(city)}
                    >
                      {city}
                    </SuggestionItem>
                  ))
                ) : (
                  <NoResults>No cities found</NoResults>
                )}
              </SuggestionsList>
            )}
          </AutocompleteContainer>
        )}

        {filterType === 'state' && (
          <AutocompleteContainer>
            <FilterLabel>Search State:</FilterLabel>
            <FilterInput
              type="text"
              placeholder="Start typing a state name..."
              value={stateSearchInput}
              onChange={handleStateInputChange}
              onFocus={() => setShowStateSuggestions(true)}
              onBlur={() => setTimeout(() => setShowStateSuggestions(false), 200)}
            />
            {showStateSuggestions && stateSearchInput && (
              <SuggestionsList>
                {filteredStates.length > 0 ? (
                  filteredStates.map(state => (
                    <SuggestionItem
                      key={state}
                      onClick={() => handleStateSelect(state)}
                    >
                      {state}
                    </SuggestionItem>
                  ))
                ) : (
                  <NoResults>No states found</NoResults>
                )}
              </SuggestionsList>
            )}
          </AutocompleteContainer>
        )}
      </FilterSection>

      {topRestrooms.length === 0 ? (
        <EmptyState>
          <EmptyIcon>🚽</EmptyIcon>
          <h3>No Reviews in Last 24 Hours!</h3>
          <p>Be the first to review a restroom and claim the throne!</p>
        </EmptyState>
      ) : (
        <LeaderboardList>
          {topRestrooms.map((restroom, index) => {
            const rank = index + 1;
            return (
              <LeaderboardItem 
                key={restroom.id} 
                rank={rank}
                onClick={() => navigate('/app', { state: { selectedRestroom: restroom, zoom: 17 } })}
              >
                <RankBadge>
                  {rank === 1 && '👑'}
                  {rank === 2 && '🥈'}
                  {rank === 3 && '🥉'}
                  {rank > 3 && `#${rank}`}
                </RankBadge>
                
                <Content>
                  <Name>{restroom.name}</Name>
                  <Details rank={rank}>
                    {restroom.address && (
                      <Detail>
                        <MapPin size={14} />
                        <span>{restroom.address}</span>
                      </Detail>
                    )}
                    {renderStars(restroom.averageRating || restroom.rating || 3)}
                  </Details>
                </Content>
                
                <Stats>
                  <StatValue>{restroom.todayReviewCount}</StatValue>
                  <StatLabel rank={rank}>
                    {restroom.todayReviewCount === 1 ? 'Review' : 'Reviews'}
                  </StatLabel>
                </Stats>
              </LeaderboardItem>
            );
          })}
        </LeaderboardList>
      )}
    </Container>
  );
}

export default PorcelainThrone;
